import React, { useEffect, useMemo, useState, useRef } from "react";
import { Container } from "@mui/material";
import { useTranslation } from "react-i18next";

import styles from "./Blogs.module.scss";

import { getBlogAll, getBlogCategories } from "../../services/apis/blog";
import {
  FilterSection,
  ModernSlider,
  SearchCountContainer,
} from "../../library/component";
import {
  Pagination,
  BlogCard,
  Loader,
  StickySearchBar,
} from "../../library/common";

import localesKeys from "../../locales/localesKeys.json";
import NoResult from "../../library/common/NoResult/NoResult";

const ITEMS_PER_PAGE = 21;

const Blogs = () => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef();

  const initBlog = {
    list: [],
    total: 0,
    loader: true,
  };
  const [blogs, setBlogs] = useState(initBlog);
  const [currentPage, setCurrentPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    categories: [],
    languages: [],
    series: [],
    expandes: ["categories"],
    search: "",
  });

  const handleFilterConfig = (key, value, filtersObj, currentPage) => {
    let newFilters = {
      ...filtersObj,
      [key]: filtersObj?.[key]?.includes(value)
        ? filtersObj?.[key]?.filter((item) => item !== value)
        : [...filtersObj[key], value],
    };
    setFilters(newFilters);
    handleGetBlogs({ newFilters, newCurrentPage: currentPage });
  };
  const filterConfig = useMemo(() => {
    const config = [
      {
        label: t(localesKeys.GRANT_LANGUAGES_CATEGORIES),
        options: categories,
        name: "categories",
        onExpand: () => {
          setFilters({
            ...filters,
            expandes: filters?.expandes?.includes("categories")
              ? filters?.expandes?.filter((item) => item !== "categories")
              : [...filters.expandes, "categories"],
          });
        },
        onCheckBoxMarked: (value) => {
          handleFilterConfig("categories", value, filters, currentPage);
        },
      },
    ];

    return config;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, filters, t]);

  const handleGetBlogs = ({
    newFilters = filters,
    newCurrentPage = currentPage,
  }) => {
    getBlogAll({
      filters: newFilters,
      page: newCurrentPage,
      limit: ITEMS_PER_PAGE,
    })
      .then((res) => {
        if (res?.success) {
          setBlogs({ ...res?.data, loader: false });
        } else {
          setBlogs({ ...initBlog, loader: false });
        }
      })
      .catch((err) => {
        setBlogs({ ...initBlog, loader: false });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  useEffect(() => {
    if (!categories?.length) {
      getBlogCategories({ type: "isBlog" })
        .then((res) => {
          if (res?.success) {
            setCategories(res?.data);
          } else {
            setCategories([]);
          }
        })
        .catch((err) => {
          setCategories([]);
        });
    }
  }, [categories?.length]);

  useEffect(() => {
    handleGetBlogs({ newFilters: filters, newCurrentPage: currentPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let startPage = Math.min(
    (currentPage - 1) * ITEMS_PER_PAGE + 1,
    blogs?.list?.length
  );
  let endPage = Math.min(currentPage * ITEMS_PER_PAGE, blogs?.total);

  return (
    <div ref={scrollContainerRef} className={styles.blogsContainer}>
      <ModernSlider path="blog" />

      <StickySearchBar
        className={"desktopSearchBar"}
        placeholder={t(localesKeys.SEARCH)}
        onSearch={(value) => {
          let newFilters = { ...filters, search: value };
          setFilters(newFilters);

          handleGetBlogs({
            newFilters: newFilters,
            newCurrentPage: currentPage,
          });
        }}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
      />

      <div className={styles.containerFluid}>
        <Container className={styles.container}>
          <div className={`${styles.booksDisplayContainer}`}>
            <FilterSection
              setFilters={setFilters}
              currentPage={currentPage}
              handleGetBooks={handleGetBlogs}
              config={filterConfig}
              filters={filters}
              isFilterOpen={isFilterOpen}
              setIsFilterOpen={setIsFilterOpen}
            />
            <div className={styles.blogsListContainer}>
              <SearchCountContainer
                startPage={startPage}
                endPage={endPage}
                total={blogs?.total}
                onSearch={(value) => {
                  let newFilters = { ...filters, search: value };
                  setFilters(newFilters);
                  handleGetBlogs({ newFilters, newCurrentPage: currentPage });
                }}
              />
              {blogs?.total ? (
                <div className={styles.gridContainer}>
                  {blogs?.list?.map((blog, index) => (
                    <>
                      <BlogCard key={index} {...blog} />
                    </>
                  ))}
                </div>
              ) : (
                <NoResult />
              )}
              <Pagination
                total={blogs?.total ? blogs?.total : 0}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                setCurrentPage={(n) => {
                  setCurrentPage(n);
                  handleGetBlogs(filters, n);
                }}
                className={styles.paginationContainer}
              />
            </div>
          </div>
          {blogs?.loader && <Loader />}
        </Container>
      </div>
    </div>
  );
};

export default Blogs;
